import React, { useMemo } from 'react';
import { Box, Typography, LinearProgress, Tooltip, Tabs, Tab } from '@mui/material';
import { AttachMoney, AccessTime } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetRetainers } from '../redux/sagas/requests/reports';
import { toLocalDay } from '../utils/dateUtils';

const BudgetList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const BudgetCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    '&:last-child': {
        marginBottom: 0,
    },
}));

const CardHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
});

const ClientName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
}));

const BudgetStatus = styled(Typography)(({ theme, status }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color:
        status === 'absorbed' ? theme.palette.error.main :
        status === 'overage' ? theme.palette.info.main :
        status === 'warning' ? theme.palette.warning.main :
        theme.palette.success.main,
}));

const MetricRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

const MetricHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
});

const MetricLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const MetricValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
}));

const StyledProgress = styled(LinearProgress)(({ theme, status }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.surface.track,
    '& .MuiLinearProgress-bar': {
        borderRadius: 4,
        backgroundColor:
            status === 'absorbed' ? theme.palette.error.main :
            status === 'overage' ? theme.palette.info.main :
            status === 'warning' ? theme.palette.warning.main :
            theme.palette.success.main,
    },
}));

const SummaryBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
}));

const SummaryItem = styled(Box)({
    textAlign: 'center',
});

const SummaryValue = styled(Typography)(({ theme, color }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.lg,
    color: color || theme.palette.primary.main,
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(AttachMoney)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));


const formatHours = (hours) => {
    return `${Math.round(hours || 0)}h`;
};

const TabLabel = styled(Tab)(({ theme }) => ({
    minHeight: theme.spacing(4),
    padding: theme.spacing(0, 1, 1, 1),
    minWidth: 0,
    fontSize: theme.typography.size.xs,
}));

const SortTabs = styled(Tabs)({
    minHeight: '36px',
});

const STATUS_ORDER = { absorbed: 0, overage: 1, warning: 2, success: 3 };

const SORT_MODES = {
    name: { label: 'Name (A–Z)', hint: 'List every retainer alphabetically.' },
    attention: { label: 'Needs attention', hint: 'Put the retainers past their pool or closest to it first.' },
};

const DEFAULT_SORT = 'name';

const STATUS_LABEL = {
    absorbed: 'Over — Absorbed',
    overage: 'Over — Billing',
    warning: 'Near Limit',
    success: 'On Track',
};

const STATUS_HINT = {
    absorbed: (over) => `${over.toFixed(1)} hours past the pool. Overage billing is off for this retainer, so those hours are worked at no charge.`,
    overage: (over) => `${over.toFixed(1)} hours past the pool. Overage billing is on, so those hours are charged on top of the retainer.`,
    warning: () => 'Close to the contracted hours for this period.',
    success: () => 'Within the contracted hours for this period.',
};

const BudgetTrackerWidget = ({ widgetId, config = {}, onConfigChange, onRemove }) => {
    // Calculate current month's date range
    const { startDate, endDate } = useMemo(() => {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        return {
            startDate: toLocalDay(startOfMonth),
            endDate: toLocalDay(endOfMonth),
        };
    }, []);

    const { data: retainers, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetRetainers(startDate, endDate),
        cacheKey: `retainers-${startDate}-${endDate}`,
        transform: (response) => response?.retainers || response || [],
    });

    const sortMode = SORT_MODES[config.sort] ? config.sort : DEFAULT_SORT;

    const sortTabs = (
        <SortTabs value={sortMode} onChange={(event, mode) => onConfigChange && onConfigChange({ sort: mode })}>
            {Object.entries(SORT_MODES).map(([mode, { label, hint }]) => (
                <TabLabel key={mode} value={mode} label={label} title={hint} />
            ))}
        </SortTabs>
    );

    const flattenedRetainers = useMemo(() => {
        if (!retainers || retainers.length === 0) return [];

        return retainers
            .map((item) => {
                const hoursUsed = (item.time || 0) / 60;
                const hoursContracted = item.billingTerms?.hours || 0;
                const overageAllowed = !!item.billingTerms?.overage;
                const overPool = hoursContracted > 0 && hoursUsed > hoursContracted;

                let status;
                if (overPool) {
                    status = overageAllowed ? 'overage' : 'absorbed';
                } else if (hoursContracted > 0 && (hoursUsed / hoursContracted) * 100 >= 80) {
                    status = 'warning';
                } else {
                    status = 'success';
                }

                return {
                    _id: item._id,
                    name: item.name,
                    parentName: item.type === 'Group' ? item.accountName || item.clientName : null,
                    hoursUsed,
                    hoursContracted,
                    hoursOver: overPool ? hoursUsed - hoursContracted : 0,
                    status,
                };
            })
            .sort((a, b) => {
                if (sortMode === 'attention') {
                    const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
                    if (byStatus !== 0) return byStatus;
                    const aShare = a.hoursContracted > 0 ? a.hoursUsed / a.hoursContracted : 0;
                    const bShare = b.hoursContracted > 0 ? b.hoursUsed / b.hoursContracted : 0;
                    if (aShare !== bShare) return bShare - aShare;
                }
                return a.name.localeCompare(b.name);
            });
    }, [retainers, sortMode]);

    const totalUsed = flattenedRetainers.reduce((sum, r) => sum + r.hoursUsed, 0);
    const totalContracted = flattenedRetainers.reduce((sum, r) => sum + r.hoursContracted, 0);
    const absorbedCount = flattenedRetainers.filter((r) => r.status === 'absorbed').length;
    const overageCount = flattenedRetainers.filter((r) => r.status === 'overage').length;

    const hasData = flattenedRetainers.length > 0;

    return (
        <BaseWidget
            title="Budget Tracker"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
            actions={onConfigChange ? sortTabs : null}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        No retainer data available
                    </Typography>
                </EmptyState>
            ) : (
                <Box>
                    <SummaryBox>
                        <SummaryItem>
                            <SummaryValue>{formatHours(totalUsed)}</SummaryValue>
                            <SummaryLabel>Used</SummaryLabel>
                        </SummaryItem>
                        <SummaryItem>
                            <SummaryValue>{formatHours(totalContracted)}</SummaryValue>
                            <SummaryLabel>Contracted</SummaryLabel>
                        </SummaryItem>
                        <Tooltip title={`${overageCount} past the pool and billing overage, ${absorbedCount} past the pool with no overage billing so the extra hours are absorbed.`}>
                            <SummaryItem>
                                <SummaryValue color={absorbedCount > 0 ? '#f44336' : '#4caf50'}>
                                    {overageCount + absorbedCount}
                                </SummaryValue>
                                <SummaryLabel>Past Pool</SummaryLabel>
                            </SummaryItem>
                        </Tooltip>
                    </SummaryBox>

                    <BudgetList>
                        {flattenedRetainers.map((retainer, index) => {
                            const { hoursUsed, hoursContracted, hoursOver, status } = retainer;
                            const hoursPercentage = hoursContracted > 0 ? Math.min(100, (hoursUsed / hoursContracted) * 100) : 0;

                            return (
                                <BudgetCard key={retainer._id || index}>
                                    <CardHeader>
                                        <ClientName>{retainer.parentName ? `${retainer.name} · ${retainer.parentName}` : retainer.name}</ClientName>
                                        <Tooltip title={STATUS_HINT[status](hoursOver)}>
                                            <BudgetStatus status={status}>{STATUS_LABEL[status]}</BudgetStatus>
                                        </Tooltip>
                                    </CardHeader>

                                    <MetricRow>
                                        <MetricHeader>
                                            <MetricLabel>
                                                <AccessTime fontSize="small" />
                                                Hours
                                            </MetricLabel>
                                            <MetricValue>
                                                {formatHours(hoursUsed)} / {formatHours(hoursContracted)}
                                            </MetricValue>
                                        </MetricHeader>
                                        <StyledProgress
                                            variant="determinate"
                                            value={hoursPercentage}
                                            status={status}
                                        />
                                    </MetricRow>
                                </BudgetCard>
                            );
                        })}
                    </BudgetList>
                </Box>
            )}
        </BaseWidget>
    );
};

export default BudgetTrackerWidget;
